import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import de from "../locales/de";
import { reportError } from "../lib/reportError";

export const LANGUAGE_STORAGE_KEY = "evi_language";
const LanguageContext = createContext(null);
const choices = ["auto", "de", "en"];

const browserLanguage = () => {
  if (typeof navigator === "undefined") return "en";
  for (const locale of navigator.languages?.length ? navigator.languages : [navigator.language]) {
    const language = locale?.toLowerCase().split(/[-_]/)[0];
    if (language === "de" || language === "en") return language;
  }
  return "en";
};

const savedPreference = () => {
  try {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return choices.includes(saved) ? saved : "auto";
  } catch (error) {
    reportError("Language preference could not be read", error);
    return "auto";
  }
};

export const LanguageProvider = ({ children }) => {
  const [preference, setPreference] = useState(savedPreference);
  const [detected, setDetected] = useState(browserLanguage);
  const language = preference === "auto" ? detected : preference;

  useEffect(() => {
    const detect = () => setDetected(browserLanguage());
    const sync = (event) => {
      if (event.key === LANGUAGE_STORAGE_KEY || event.key === null) setPreference(savedPreference());
    };
    window.addEventListener("languagechange", detect);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("languagechange", detect);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const selectLanguage = useCallback((value) => {
    if (!choices.includes(value)) return;
    setDetected(browserLanguage());
    setPreference(value);
    // Only allowlisted locale codes are persisted, never customer data or credentials.
    try { localStorage.setItem(LANGUAGE_STORAGE_KEY, value); }
    catch (error) { reportError("Language preference could not be saved", error); }
  }, []);

  const t = useCallback((text, values = {}) => {
    const translated = language === "de" ? de[text] ?? text : text;
    return translated?.replace(/\{\{(\w+)\}\}/g, (match, key) => values[key] ?? match);
  }, [language]);

  useEffect(() => {
    document.documentElement.lang = language;
    document.title = t("Evi-Studio — Custom Songs, Jingles & Albums");
    document.querySelector('meta[name="description"]')?.setAttribute("content", t("Personal songs, radio jingles and album productions. Your story, professionally produced by Evi-Studio."));
  }, [language, t]);

  const value = useMemo(() => ({ language, preference, selectLanguage, t }), [language, preference, selectLanguage, t]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);